import { useEffect, useRef, useState } from 'react';
import type { TimesheetEntry } from '@/types/timesheet';
import type { TimesheetGridCore } from './useTimesheetGridCore';
import { useTimesheetBulkEntryTools } from './useTimesheetBulkEntryTools';
import { useTimesheetDirtyState } from './useTimesheetDirtyState';
import { useTimesheetOvertimeClassification } from './useTimesheetOvertimeClassification';
import { useTimesheetResubmit } from './useTimesheetResubmit';
import { useTimesheetRowActions } from './useTimesheetRowActions';
import { useTimesheetSubmission } from './useTimesheetSubmission';
import { useTimesheetWeekNavigation } from './useTimesheetWeekNavigation';

/**
 * Every user action the grid supports — row edits, bulk fills, overtime
 * classification, saving/submitting, recall and week navigation — plus the
 * transient UI state those actions drive.
 */
export function useTimesheetGridActions(core: TimesheetGridCore) {
  const {
    entries, setEntries, currentTimesheet, currentEmployee, organizationSettings,
    permissions, screenMode, isReadOnly, isDaily, data, overtime, validation,
  } = core;

  // Always-current ref so event handlers (navigateWeek, saveTimesheet) never
  // read stale entries from a closed-over render snapshot.
  const entriesRef = useRef<TimesheetEntry[]>(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [showAIInsightsSheet, setShowAIInsightsSheet] = useState(false);
  // Scheduled booking / absence dialog selections
  const [selectedBookingForDetails, setSelectedBookingForDetails] = useState<TimesheetEntry | null>(null);
  const [selectedBookingForDismiss, setSelectedBookingForDismiss] = useState<TimesheetEntry | null>(null);
  const [selectedAbsenceForDetails, setSelectedAbsenceForDetails] = useState<TimesheetEntry | null>(null);
  // Track editing state for hours inputs to prevent premature autocomplete
  const [editingHours, setEditingHours] = useState<Record<string, string | undefined>>({});
  const [openNoteCell, setOpenNoteCell] = useState<string | null>(null); // `${entryId}-${day}`
  const [draftNote, setDraftNote] = useState<string>('');

  const rowActions = useTimesheetRowActions({
    entries,
    setEntries,
    targetEmployee: core.targetEmployee,
    canDeleteTimesheet: permissions.canDeleteTimesheet,
    tableScrollRef: core.tableScrollRef,
  });

  const bulkTools = useTimesheetBulkEntryTools({
    entries,
    setEntries,
    timesheets: core.timesheets,
    targetEmployeeId: core.targetEmployeeId,
    currentWeekStart: core.currentWeekStart,
    allAvailableProjects: data.allAvailableProjects,
    organizationSettings,
    isDaily,
  });

  const classification = useTimesheetOvertimeClassification({
    entries,
    setEntries,
    overtimeEnabled: overtime.overtimeEnabled,
    isReadOnly,
  });

  const isDirty = useTimesheetDirtyState({ entries, currentTimesheet, isReadOnly });

  const submission = useTimesheetSubmission({
    screenMode,
    entries,
    entriesRef,
    currentTimesheet,
    currentEmployee,
    employees: core.employees,
    timesheets: core.timesheets,
    updateTimesheet: core.updateTimesheet,
    organizationSettings,
    effectiveWorkingPattern: core.effectiveWorkingPattern,
    companyHasCostingIntegration: core.currentCompany?.hasCostingIntegration === true,
    hasBlockingPolicyViolation: core.aiWarningsState.hasBlockingPolicyViolation,
    hasMissingDescriptions: validation.hasMissingDescriptions,
    isDaily,
    autoSaveTimerRef: core.autoSaveTimerRef,
    setLastSaved: core.setLastSaved,
  });

  const resubmit = useTimesheetResubmit({
    screenMode,
    entries,
    currentTimesheet,
    currentEmployee,
    updateTimesheet: core.updateTimesheet,
    organizationSettings,
    pendingRejection: core.pendingRejection,
    hasBlockingPolicyViolation: core.aiWarningsState.hasBlockingPolicyViolation,
    hasMissingDescriptions: validation.hasMissingDescriptions,
    hasMissingCostCentres: validation.hasMissingCostCentres,
    isDaily,
    isSaving: submission.isSaving,
    isSubmitting: submission.isSubmitting,
    setIsSubmitting: submission.setIsSubmitting,
  });

  const weekNavigation = useTimesheetWeekNavigation({
    currentWeekStart: core.currentWeekStart,
    setCurrentWeekStart: core.setCurrentWeekStart,
    weekStartDay: core.weekStartDay,
    organizationSettings,
    currentTimesheet,
    entriesRef,
    updateTimesheet: core.updateTimesheet,
    isViewingOtherResource: core.isViewingOtherResource,
    isReadOnly,
    isSaving: submission.isSaving,
    isSubmitting: submission.isSubmitting,
    autoSaveTimerRef: core.autoSaveTimerRef,
    setLastSaved: core.setLastSaved,
  });

  return {
    entriesRef,
    showAuditHistory,
    setShowAuditHistory,
    showAIInsightsSheet,
    setShowAIInsightsSheet,
    selectedBookingForDetails,
    setSelectedBookingForDetails,
    selectedBookingForDismiss,
    setSelectedBookingForDismiss,
    selectedAbsenceForDetails,
    setSelectedAbsenceForDetails,
    editingHours,
    setEditingHours,
    openNoteCell,
    setOpenNoteCell,
    draftNote,
    setDraftNote,
    rowActions,
    bulkTools,
    classification,
    isDirty,
    submission,
    resubmit,
    weekNavigation,
  };
}

export type TimesheetGridActions = ReturnType<typeof useTimesheetGridActions>;
